import { useEffect, useRef, useState } from "react";
import { useEngineContext } from "../app/EngineProvider";
import type { EngineSnapshot } from "../state/types";

const UI_UPDATE_INTERVAL_MS = 100; // 10 Hz

function idleSnapshot(bpm: number, seed: string): EngineSnapshot {
  return {
    status: "idle",
    isResuming: false,
    bpm,
    autoTempo: false,
    bar: 0,
    beat: 0,
    resumeBar: 0,
    seed,
    diagnostics: {
      audioContextState: "suspended",
      baseLatency: 0,
      outputLatency: null,
      lookAhead: 0,
      transportCallbackCount: 0,
      plannedBarCount: 0,
      pendingDispatchEventCount: 0,
      activeVoiceCount: 0,
      nodeCountEstimate: 0,
      dispatchedEventTotal: 0,
      lateDropCount: 0,
      masterAppliedGain: 0,
      masterMuteReason: "starting",
      resumeDrainRemainingMs: 0,
      uiFps: 0,
      dispatchTickHz: 0,
      lastError: null,
      genreNormalized: { ambient: 1 / 3, techno: 1 / 3, jazz: 1 / 3 },
      currentEnergy: 0,
      currentDensity: 0,
      currentSection: "intro",
      rhythmPatternHash: "-",
      activeRhythmRoles: [],
      eventsThisBar: 0,
      averageVelocity: 0,
      averageTimingOffsetMs: 0,
    },
  };
}

/**
 * Polls the engine at 10 Hz via requestAnimationFrame.
 *
 * Deliberately a pull, not a push: the audio side must never trigger a React
 * render per event, and UI work must not sit on the scheduling path.
 */
export function useEngineSnapshot(): EngineSnapshot {
  const { getEngine, store } = useEngineContext();
  const initial = store.getSnapshot();
  const [snapshot, setSnapshot] = useState<EngineSnapshot>(() =>
    idleSnapshot(initial.transport.bpmManual, initial.seed),
  );
  const frameRef = useRef<number>(0);
  const lastSampleRef = useRef<number>(0);
  const framesRef = useRef<number>(0);
  const fpsWindowRef = useRef<number>(0);
  const fpsRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;

    const loop = (nowMs: number) => {
      if (cancelled) return;

      framesRef.current += 1;
      if (fpsWindowRef.current === 0) fpsWindowRef.current = nowMs;
      const fpsElapsed = nowMs - fpsWindowRef.current;
      if (fpsElapsed >= 500) {
        fpsRef.current = (framesRef.current * 1000) / fpsElapsed;
        framesRef.current = 0;
        fpsWindowRef.current = nowMs;
      }

      if (nowMs - lastSampleRef.current >= UI_UPDATE_INTERVAL_MS) {
        lastSampleRef.current = nowMs;
        const engine = getEngine();
        const controls = store.getSnapshot();
        setSnapshot(
          engine === null
            ? idleSnapshot(controls.transport.bpmManual, controls.seed)
            : engine.readSnapshot(Math.round(fpsRef.current)),
        );
      }
      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameRef.current);
    };
  }, [getEngine, store]);

  return snapshot;
}
